import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";

export type AuthEvent = "INITIAL_SESSION" | "SIGNED_IN" | "SIGNED_OUT" | "TOKEN_REFRESHED" | "USER_UPDATED" | "PASSWORD_RECOVERY" | "MFA_CHALLENGE_VERIFIED" | string;
export type AuthChangeCallback = (session: Session | null, event?: AuthEvent) => void;
const ADMIN_RPC_TIMEOUT_MS = 10000;

export async function signIn(email: string, password: string) {
  return supabase.auth.signInWithPassword({ email, password });
}

export async function signOut() {
  await supabase.auth.signOut();
}

export async function getSession() {
  const { data, error } = await supabase.auth.getSession();
  if (error) throw error;
  return data.session;
}

export async function isAdmin(userId: string): Promise<boolean> {
  const roleRequest = supabase.rpc("has_role", {
    _user_id: userId,
    _role: "admin",
  });

  let timeoutId: number | undefined;

  const timeoutRequest = new Promise<never>((_, reject) => {
    timeoutId = window.setTimeout(() => {
      reject(new Error(`isAdmin timeout after ${ADMIN_RPC_TIMEOUT_MS}ms`));
    }, ADMIN_RPC_TIMEOUT_MS);
  });
  const { data, error } = await Promise.race([roleRequest, timeoutRequest]);
  if (timeoutId) {
    window.clearTimeout(timeoutId);
  }

  if (error) {
    throw error;
  }

  return data === true;
}

export function onAuthStateChange(callback: AuthChangeCallback) {
  const { data } = supabase.auth.onAuthStateChange((event, session) => {
    callback(session, event);
  });

  return data.subscription;
}

export function getUserDisplayName(user: User | null): string {
  if (!user) return "Administrador";

  const fullName = user.user_metadata?.full_name;
  if (typeof fullName === "string" && fullName.trim()) {
    return fullName.trim();
  }

  return user.email ?? "Administrador";
}
